/** Recipient autocomplete in the compose window: parsing the two sources,
 *  merging them, and highlighting what matched. Pure (no React), so it runs
 *  under `node --test`.
 *
 *  Sources, in display order:
 *    1. GET /api/mail/people             people the owner mails with, from
 *       the daemon's header cache. Ranked (people you write to first) and
 *       answered in milliseconds.
 *    2. GET /api/bridge/v1/recipients    Outlook's address lists (the
 *       directory), for people never mailed. It runs on Outlook's COM
 *       thread, so it only ever appends below the cached people and its
 *       failure hides nothing. Since 2026-10-02 the bridge reads the whole
 *       Global Address List in the background (about 107,000 entries,
 *       daily) and answers from that in well under a second; `indexing`
 *       says it is still being read, when only exact names resolve.
 *
 *  It used to be the only source, and it timed out on most keystrokes; the
 *  proxy's 502 envelope then parsed as "no hits", so the field showed no
 *  suggestions at all. */

export type SuggestionSource = "sent" | "received" | "directory";

export type Suggestion = {
  name: string;
  address: string;
  source: SuggestionSource;
  /** The address list a directory hit came from ("Global Address List"). */
  list?: string;
};

export type HighlightPart = { text: string; hit: boolean };

/** How many rows the dropdown shows at most. */
export const MAX_SUGGESTIONS = 10;

/** Loose: one @, a dot in the domain, no spaces. Exchange X.500 DNs that the
 *  bridge could not resolve to SMTP fail it and are never offered. */
export function isAddress(s: string): boolean {
  const t = s.trim();
  return !t.startsWith("/") && /^[^\s@<>"';,]+@[^\s@<>"';,]+\.[^\s@<>"';,]+$/.test(t);
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** `{ ok, data: { people: [{ name, address, source }] } }` from
 *  /api/mail/people. Anything else (an error envelope, HTML) is no hits. */
export function parsePeopleResponse(j: unknown): Suggestion[] {
  const list = (j as { data?: { people?: unknown } } | null)?.data?.people;
  if (!Array.isArray(list)) return [];
  const out: Suggestion[] = [];
  for (const p of list) {
    const o = (p ?? {}) as Record<string, unknown>;
    const address = str(o.address);
    if (!isAddress(address)) continue;
    out.push({ name: str(o.name), address, source: o.source === "sent" ? "sent" : "received" });
  }
  return out;
}

/** `{ ok, data: { hits: [{ name, address, source }] } }` from the bridge's
 *  /v1/recipients, where `source` is the address list's name. The proxy's
 *  502 `{ ok: false, error: "bridge_unreachable" }` (a timeout) is no hits. */
export function parseDirectoryResponse(j: unknown): Suggestion[] {
  const list = (j as { data?: { hits?: unknown } } | null)?.data?.hits;
  if (!Array.isArray(list)) return [];
  const out: Suggestion[] = [];
  for (const h of list) {
    const o = (h ?? {}) as Record<string, unknown>;
    const address = str(o.address);
    if (!isAddress(address)) continue;
    const listName = str(o.source);
    out.push({ name: str(o.name), address, source: "directory", ...(listName ? { list: listName } : {}) });
  }
  return out;
}

const queryTokens = (query: string): string[] =>
  query
    .trim()
    .toLowerCase()
    .split(/[\s,;]+/)
    .filter((t) => t.length > 0);

/** Whether an earlier answer still fits what is typed now. Used to narrow
 *  the list while the answer for the new text is on its way, so a fast
 *  Enter never picks someone who no longer matches. Deliberately loose
 *  (substring): the server's word-start match is stricter. */
export function stillMatches(s: Pick<Suggestion, "name" | "address">, query: string): boolean {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return false;
  const name = s.name.toLowerCase();
  const address = s.address.toLowerCase();
  return tokens.every((t) => name.includes(t) || address.includes(t));
}

/** Cached people first, then directory hits nobody listed yet, without
 *  anyone already in the field. Deduplicated by address, ignoring case. */
export function mergeSuggestions(
  people: Suggestion[],
  directory: Suggestion[],
  taken: string[],
  limit = MAX_SUGGESTIONS,
): Suggestion[] {
  const seen = new Set(taken.map((a) => a.trim().toLowerCase()));
  // The directory can list someone already suggested under another alias
  // (ben.tse@ vs btse@): one row per person, the one from your mail.
  const peopleNames = new Set(people.map((p) => personKey(p.name)).filter(Boolean));
  const out: Suggestion[] = [];
  for (const s of [...people, ...directory]) {
    if (out.length >= limit) break;
    const key = s.address.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    if (s.source === "directory" && peopleNames.has(personKey(s.name))) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** A name reduced for comparing people: lower case, letters and digits
 *  only, so "Tse, Ben" and "Ben Tse" compare equal. */
export function personKey(name: string): string {
  return name
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .sort()
    .join(" ");
}

/** What Enter / Tab / comma should pick, or null to treat the typed text
 *  as a free-typed recipient. A complete address that was typed wins over
 *  the highlighted row (the directory matches substrings, so typing
 *  "ann@example.com" can list "joann@example.com" first); it only takes a
 *  suggestion with exactly that address, to keep the person's name. */
export function suggestionToAccept(query: string, hits: Suggestion[], active: number, listShown: boolean): Suggestion | null {
  const typed = query.trim().toLowerCase();
  if (isAddress(typed)) return hits.find((h) => h.address.toLowerCase() === typed) ?? null;
  return listShown ? (hits[active] ?? null) : null;
}

const WORD_CHAR = /[\p{L}\p{N}]/u;

/** Split `text` into plain and matched parts for the dropdown. Each query
 *  word is highlighted where it starts a word of the text, the way both
 *  sources match; never mid-word ("s[ben]hur@" read as noise). */
export function highlightParts(text: string, query: string): HighlightPart[] {
  const tokens = queryTokens(query).sort((a, b) => b.length - a.length);
  if (!text || tokens.length === 0) return text ? [{ text, hit: false }] : [];
  const lower = text.toLowerCase();
  // toLowerCase can change the length of a few characters; only then fall
  // back to no highlight rather than marking the wrong letters.
  if (lower.length !== text.length) return [{ text, hit: false }];
  const covered: Array<[number, number]> = [];
  const free = (s: number, e: number) => covered.every(([a, b]) => e <= a || s >= b);
  const wordStart = (i: number) => WORD_CHAR.test(text[i] ?? "") && (i === 0 || !WORD_CHAR.test(text[i - 1]));
  for (const t of tokens) {
    let at = -1;
    for (let i = lower.indexOf(t); i >= 0; i = lower.indexOf(t, i + 1)) {
      if (wordStart(i) && free(i, i + t.length)) {
        at = i;
        break;
      }
    }
    if (at >= 0) covered.push([at, at + t.length]);
  }
  covered.sort((a, b) => a[0] - b[0]);
  const parts: HighlightPart[] = [];
  let pos = 0;
  for (const [s, e] of covered) {
    if (s > pos) parts.push({ text: text.slice(pos, s), hit: false });
    parts.push({ text: text.slice(s, e), hit: true });
    pos = e;
  }
  if (pos < text.length) parts.push({ text: text.slice(pos), hit: false });
  return parts;
}
